import React, { useEffect, useState, useRef } from "react";
import "@innovatrics/dot-document-auto-capture";
import Head from "../Head";
import Footer from "../../components/Footer";
import { useCallback, useContext } from "react";
import GlobalConfig from "../../context/GlobalConfig";
import { languageData } from "../../constants/languageData";
import "../DocumentCapture/docCapture.css";
import FaceAutoCapture from "./FaceAutoCapture";
import { useParams } from "react-router-dom";
import JourneyConfig from "../../context/JourneyConfig";
import LivenessAnalysing from "../../components/LivenessAnalysing";
import CameraLoadingOverlay from "../../components/Loader/CameraLoadingOverlay";
import { convertImageToUrl } from "../../utils/base64ToUrlUtility";
import { FileType } from "../../constants/fileTypeConstants";

/**
 * Route to the generic "Oops! Something went wrong" screen.
 * `convertImageToUrl` already retried the upload 3 times with a fresh
 * presigned URL each attempt, so reaching here is terminal.
 */
function routeToErrorScreen(stepperFunc, context = "upload") {
  console.error(`[Liveness] Routing to error screen | context=${context}`);
  sessionStorage.setItem("error", "true");
  sessionStorage.removeItem("errorCategory");
  sessionStorage.removeItem("errorReason");
  const currentStepFromStorage = sessionStorage.getItem("currentStep");
  stepperFunc(currentStepFromStorage ? parseInt(currentStepFromStorage) : 0);
}

const Liveness = ({ stepperFunc, steps, currentStep, currentDocumentStep }) => {
  const { config } = useContext(GlobalConfig);
  const { journey, setJourney, updateJourney, getJourney } =
    useContext(JourneyConfig);
  const [blobImage, setBlobImage] = useState();
  const [loading, setIsLoading] = useState(false);
  const [imageCaptured, setImageCaptured] = useState(false);
  const [captureStatus, setCaptureStatus] = useState("image_not_present");
  const [isCameraRunning, setIsCameraRunning] = useState(false);
  const [multipleFacesDetected, setMultipleFacesDetected] = useState(false);
  const timeoutRef = useRef(null);
  const language = sessionStorage.getItem("language") ?? "en";
  const { journeyId } = useParams();
  const streamRef = useRef(null);
  const color = config?.branding?.accentColor;
  const bgColor =
    "color-mix(in srgb, " + config?.branding?.accentColor + " 10%, white)";

  const instructionChangeEvent = "face-auto-capture:instruction-changed";

  const stopSafeStreamFromRef = useCallback(() => {
    console.log('[Liveness][safeStream] Attempting to stop stream from streamRef');

    const stream = streamRef?.current;
    if (!stream) {
      console.log('[Liveness][safeStream] No stream found in streamRef - skipping');
      return;
    }

    try {
      const tracks = stream.getTracks();
      console.log(`[Liveness][safeStream] Found ${tracks.length} tracks to stop`);

      tracks.forEach((track) => {
        console.log(`[Liveness][safeStream] Stopping ${track?.kind} track (ID: ${track?.id})`);
        track.stop();
        console.log(`[Liveness][safeStream] Track stopped: ${track?.readyState}`);
      });

      streamRef.current = null;
      console.log('[Liveness][safeStream] Stream cleanup complete and ref cleared');
    } catch (e) {
      console.error('[Liveness][safeStream] Stream cleanup failed:', e);
    }
  }, []);

  useEffect(() => {
    const cleanupCameras = async () => {
      try {
        stopSafeStreamFromRef();
        console.log('[Liveness][cleanupCameras] stream cleared without error');
      } catch (error) {
        console.log("[Liveness][cleanupCameras] Error during clean-up : ", error)
      }
    };
    return () => {
      cleanupCameras().catch(error => {
        console.error('[Liveness][cleanupCameras] Cleanup error:', error);
      });
    };
  }, []);
  useEffect(() => {

    const handleStateChanged = (event) => {
      if (!event.detail) {
        console.warn('[Liveness][CameraReadyState] Received state change event without details', event);
        return;
      }

      console.log(`[Liveness][CameraReadyState] State changed: ${event.detail.appState}`, JSON.stringify(event.detail));

      if (event.detail.appState === 'RUNNING') {
        console.log('[Liveness][CameraReadyState] 🎥 Camera is now operational');
        const element = document.getElementById('x-dot-face-auto-capture');
        streamRef.current = element?.firstElementChild?.querySelector('video')?.srcObject;
      }
    };

    console.log('[Liveness][CameraReadyState] Adding event listener...');
    document.addEventListener(
      'face-auto-capture:state-changed',
      handleStateChanged
    );

    return () => {
      console.log('[Liveness][CameraReadyState] Cleaning up...');
      document.removeEventListener(
        'face-auto-capture:state-changed',
        handleStateChanged
      );
    };
  }, []);

  useEffect(() => {
    const cleanupCameras = async () => {
      try {
        sessionStorage.removeItem('isCameraPage');
        stopSafeStreamFromRef();
        console.log('[Liveness][cleanupCameras] stream cleared without error');
      } catch (error) {
        console.log("[Liveness][cleanupCameras] Error during clean-up : ", error)
      }
    };
    sessionStorage.setItem('isCameraPage', 'true');
    return () => {
      cleanupCameras().catch(error => {
        console.error('[Liveness][cleanupCameras] Cleanup error:', error);
      });
    };
  }, []);



  useEffect(() => {
    // Set up 5-second fallback
    timeoutRef.current = setTimeout(() => {
      if (!isCameraRunning) {
        console.log('[Liveness][CameraReadyState] ⏱️ Fallback triggered - marking camera as ready after timeout');
        setIsCameraRunning(true);
      }
    }, 5000);

    const handleInstructionChange = (event) => {
      setCaptureStatus(event.detail.instructionCode ?? "face_centering");
    };

    const handleStateChanged = (event) => {
      if (!event.detail) {
        console.warn('[Liveness][CameraReadyState] Received state change event without details', event);
        return;
      }
      console.log(`[Liveness][CameraReadyState] State changed: ${event?.detail?.appState}`, JSON.stringify(event?.detail));
      if (event?.detail?.appState === 'RUNNING') {
        const element = document.getElementById('x-dot-face-auto-capture');
        streamRef.current = element?.firstElementChild?.querySelector('video')?.srcObject;
        console.log('[Liveness][CameraReadyState] 🎥 Camera is now operational');
        setIsCameraRunning(true);

        if (timeoutRef.current) {
          console.log('[Liveness][CameraReadyState] Clearing fallback timeout');
          clearTimeout(timeoutRef.current);
        }
      } else if (event?.detail?.appState === 'ERROR') {
        console.log('[Liveness][CameraReadyState] Error occured during camera initialisation . setting cameraRunning state to true after 2 second delay');

        // Set cameraRunning to true after 2 seconds if it's still false
        setTimeout(() => {
          if (!isCameraRunning) {
            console.log('[Liveness][CameraReadyState] 2 second delay completed, setting cameraRunning to true');
            setIsCameraRunning(true);
          } else {
            console.log('[Liveness][CameraReadyState] 2 second delay completed, but cameraRunning is already true, skipping');
          }
        }, 2000);

        // Show error message by removing display none from p tag
        setTimeout(() => {
          const faceAutoCaptureUI = document.getElementById('x-dot-face-auto-capture-ui');
          if (faceAutoCaptureUI) {
            const errorParagraph = faceAutoCaptureUI.querySelector('p');
            if (errorParagraph) {
              errorParagraph.style.display = 'block';
              console.log('[Liveness][Error] Showing error message in face auto-capture UI');
            }
          }
        }, 50);
      }
    };

    document.addEventListener(instructionChangeEvent, handleInstructionChange);
    document.addEventListener('face-auto-capture:state-changed', handleStateChanged);

    return () => {
      console.log('[Liveness][CameraReadyState] Cleaning up...');
      if (timeoutRef.current) {
        console.log('[Liveness][CameraReadyState] Clearing pending timeout');
        clearTimeout(timeoutRef.current);
      }
      document.removeEventListener(instructionChangeEvent, handleInstructionChange);
      document.removeEventListener('face-auto-capture:state-changed', handleStateChanged);
    };
  }, []);

  useEffect(() => {
    if (journeyId && !journey) getJourney(journeyId);
  }, [journeyId]);

  const handleError = useCallback((error) => {
    console.log("error received is", error);
  }, []);

  const handleFaceCapturePhotoTaken = (imageData, content) => {
    setImageCaptured(true);
    setBlobImage(imageData.image);
    setJourney({
      ...journey,
      capturedData: {
        ...journey?.capturedData,
        images: {
          ...journey?.capturedData?.images,
          selfie: imageData,
        },
      },
    });
    //blobToBase64(imageData?.image);
  };

  function blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
    });
  }



  const navigateToImageCheck = () => {
    setTimeout(() => {
      stepperFunc(steps[0]);
    }, 2000);
  };

  useEffect(() => {
    const func = async () => {
      if (!(blobImage && journey?.capturedData?.images?.selfie)) return;
      setIsLoading(true);
      sessionStorage.removeItem('isCameraPage');
      try {
        const base64Image = await blobToBase64(blobImage);
        // 3 internal retries with a fresh presigned URL each attempt
        // happen inside convertImageToUrl. A non-null error here is
        // terminal.
        const { s3Key, error } = await convertImageToUrl(base64Image, journeyId, FileType.SELFIE);

        // Rate-limit cap hit → backend has terminated the journey.
        // Surface the existing isJourneyDone signal so Settings.jsx
        // routes to the VerificationTerminated screen.
        if (error && error.terminateJourney) {
          try {
            await updateJourney("FACE_CAPTURE", {}, currentStep);
          } catch (signalErr) {
            console.error("[Liveness] Failed to surface terminated state", signalErr);
          }
          setIsLoading(false);
          return;
        }

        // Any other upload error → fall back to embedding the raw base64
        // in update-journey (s3Key is "" here, so the imageBase64 spread
        // below engages) instead of dead-ending on the error screen.
        if (error) {
          console.warn(`[Liveness] Upload failed (${error.code}) — falling back to base64 payload`);
        }

        const response = await updateJourney(
          "FACE_CAPTURE",
          {
            image: s3Key,
            ...(s3Key === "" && { imageBase64: base64Image }),
            currentDocumentStep: currentDocumentStep + 1
          },
          currentStep
        );
        // Robust multiple-faces check on the API response
        const faceCountMessage = response?.faceCountMessage;
        const isDeepFakeAiGenerated = response?.deepfakeAnalysis?.isAiGenerated;
        const shouldTriggerError =
          (response?.isFaceCovered && response?.faceDetections) ||
          response?.passiveLiveliness?.liveness === false ||
          response?.faceMatch === false ||
          response?.faceMatch2 === false ||
          isDeepFakeAiGenerated === true ||
          faceCountMessage || response?.eyeClosureDetected;
        if (shouldTriggerError) {
          sessionStorage.setItem("error", "true");
          sessionStorage.setItem("errorCategory", "livenessCheckError");
          sessionStorage.setItem("errorReason", JSON.stringify(response));
          const currentStepFromStorage = sessionStorage.getItem("currentStep");
          stepperFunc(
            currentStepFromStorage ? parseInt(currentStepFromStorage) : 0
          );
        } else {
          navigateToImageCheck();
        }
      } catch (err) {
        console.error("[Liveness] Selfie upload / update-journey failed", err);
        setIsLoading(false);
        routeToErrorScreen(stepperFunc, "updateJourney");
      }
    };
    func();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blobImage, journey?.capturedData?.images]);

  if (loading) return <LivenessAnalysing />;

  return (
    <>
      <Head
        stepperFunc={stepperFunc}
        showLanguages={false}
        showBackIcon={false}
        style={{ zIndex: "100" }}
      />
      <div className="parent-container">
        <div className="main">
          <div className="left-section">
            <CameraLoadingOverlay isVisible={!isCameraRunning} />
            <div
              style={{
                position: "relative",
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <FaceAutoCapture
                onPhotoTaken={handleFaceCapturePhotoTaken}
                onError={handleError}
                onMultipleFacesChange={setMultipleFacesDetected}
              />
            </div>
          </div>
          <div
            className="right-section md:items-start items-center"
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              gap: 24,
              fontFamily: "Inter, sans-serif",
            }}
          >
            <>
              <div>
                <div className="flex flex-col md:mt-[130px]">
                  <div className="flex justify-center md:justify-start">
                    <svg
                      width="44"
                      height="44"
                      viewBox="0 0 44 44"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle
                        cx="22"
                        cy="22"
                        r="22"
                        fill={bgColor}
                        style={{ background: bgColor }}
                      />
                      <g clip-path="url(#clip0_1556_28470)">
                        <path
                          d="M15 25.5H13V31.5H19V29.5H15V25.5ZM15 15.5H19V13.5H13V19.5H15V15.5ZM31 13.5H25V15.5H29V19.5H31V13.5ZM29 29.5H25V31.5H31V25.5H29V29.5ZM22 18.5C19.79 18.5 18 20.29 18 22.5C18 24.71 19.79 26.5 22 26.5C24.21 26.5 26 24.71 26 22.5C26 20.29 24.21 18.5 22 18.5ZM22 24.5C20.9 24.5 20 23.6 20 22.5C20 21.4 20.9 20.5 22 20.5C23.1 20.5 24 21.4 24 22.5C24 23.6 23.1 24.5 22 24.5Z"
                          fill="#1658aa"
                          style={{ fill: color }}
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_1556_28470">
                          <rect
                            width="24"
                            height="24"
                            fill="white"
                            transform="translate(10 10.5)"
                          />
                        </clipPath>
                      </defs>
                    </svg>
                  </div>
                  {multipleFacesDetected ? (
                    <div
                      className="md:mt-3 mt-6 text-[18px] md:text-[28px] font-semibold text-[#1658aa] text-center md:text-left mb-6 md:mb-0"
                      style={{
                        color: color,
                        fontWeight: 600,
                        lineHeight: "45px",
                        fontFamily: "Inter, sans-serif",
                      }}
                    >
                      {languageData[language]?.livenessCheckError
                        ?.passiveLiveliness?.mutipleFaces?.heading ??
                        languageData["en"]?.livenessCheckError
                          ?.passiveLiveliness?.mutipleFaces?.heading}
                    </div>
                  ) : (
                    <div
                      className="md:mt-3 mt-6 text-[18px] md:text-[28px] font-semibold text-[#1658aa] text-center md:text-left mb-6 md:mb-0"
                      style={{
                        color: color,
                        fontWeight: 600,
                        lineHeight: "45px",
                        fontFamily: "Inter, sans-serif",
                      }}
                    >
                      {imageCaptured
                        ? languageData[language]?.videoCheckClearList
                          ?.default?.description ??
                        languageData["en"]?.videoCheckClearList?.default
                          ?.description
                        : config?.faceCapture?.enableFaceCaptureConfidence
                          ? (languageData[language]?.videoCheckListConfidence?.[captureStatus]?.description ??
                            languageData["en"]?.videoCheckListConfidence?.[captureStatus]?.description ??
                            languageData[language]?.videoCheckList?.[captureStatus]?.description ??
                            languageData["en"]?.videoCheckList?.[captureStatus]?.description)
                          : (languageData[language]?.videoCheckList?.[captureStatus]?.description ??
                            languageData["en"]?.videoCheckList?.[captureStatus]?.description)}
                    </div>
                  )}
                </div>
              </div>
              <Footer />
            </>
          </div>
        </div>
      </div>
    </>
  );
};

export default Liveness;
